import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompositeFilterDescriptor, orderBy } from '@progress/kendo-data-query';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import { RowMenuAction } from '@components-clean/core/grid/grid.interface';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { ReportDefinitionFields } from '@queries/generated/fragments.generated';

interface ExcelListingsGridProps {
    reports: ReportDefinitionFields[];
    actions: RowMenuAction[];
    title: string;
    showAdd?: boolean;
    addCallback?: () => void;
    downloadingReport?: boolean;
    exportFileName: string;
    canEdit?: boolean;
}

interface CustomReportGridItem extends ReportDefinitionFields {
    fullName: string;
    createdByFullName?: string;
    updatedByFullName?: string;
}

const ExcelListingsGrid = ({
    reports,
    actions,
    title,
    showAdd,
    addCallback,
    downloadingReport,
    exportFileName,
    canEdit = false,
}: ExcelListingsGridProps) => {
    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('ram', { keyPrefix: 'reporting' });
    const { TooltipCell } = createGridCustomCells();

    const [gridItems, setGridItems] = useState([] as CustomReportGridItem[]);
    const [filterState, setFilterState] = useState<CompositeFilterDescriptor | undefined>({
        logic: 'and',
        filters: [],
    });

    const getGridItems = (): CustomReportGridItem[] => {
        return reports.map((report: ReportDefinitionFields) => {
            const fullName = report.includeInternalRisks ? `INTERNAL ${report.name}` : report.name;

            return {
                ...report,
                fullName,
                createdByFullName: report.createdBy ? `${report.createdBy.firstName} ${report.createdBy.lastName}` : '',
                updatedByFullName: report.updatedBy ? `${report.updatedBy.firstName} ${report.updatedBy.lastName}` : '',
            } as CustomReportGridItem;
        });
    };

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        if (gridItems.length > 0 && reports.length > gridItems.length) {
            setFilterState({ logic: 'and', filters: [] });
        }
        if (reports) setGridItems(orderBy(getGridItems(), [{ field: 'fullName', dir: 'asc' }]));
        /* eslint-enable @eslint-react/set-state-in-effect */
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [reports]);

    const columns: GridColumnProps[] = [
        {
            field: 'fullName',
            title: tRoot('admin:customReportName'),
            id: 'custom-report-name',
            minWidth: 180,
            lockedByDefault: true,
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            field: 'description',
            title: tRoot('admin:customReportDescription'),
            id: 'custom-report-description',
            minWidth: 180,
            lockedByDefault: true,
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            field: 'createdByFullName',
            title: t('excelListings.grid.columns.createdBy'),
            minWidth: 150,
        },
        {
            field: 'created',
            title: t('excelListings.grid.columns.created'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 80,
            cellName: 'DateCell',
        },
        {
            field: 'updatedByFullName',
            title: t('excelListings.grid.columns.updatedBy'),
            minWidth: 150,
        },
        {
            field: 'updated',
            title: t('excelListings.grid.columns.updated'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 80,
            cellName: 'DateCell',
        },
        {
            field: 'recordId',
            title: 'ID',
            minWidth: 120,
            id: 'custom-report-id',
            filter: 'numeric',
            showByDefault: false,
        },
    ];

    return (
        <Grid
            excludeHiddenRowMenuActions
            title={title}
            maxHeight="auto"
            testDataId="ram-reporting-excellistings"
            data={gridItems}
            columns={columns}
            filterable
            showAddIcon={showAdd}
            addCallback={addCallback}
            addRecordTooltip={t('excelListings.grid.addRecordLabel')}
            useLoadingOverlay
            loading={downloadingReport}
            canEdit={canEdit}
            scrollable="virtual"
            dataItemKey="id"
            exportFilename={exportFileName}
            service={AuditServiceType.CORE}
            area={AuditApplicationArea.RISK_ASSESSMENT}
            dataType={AuditDataType.EXCEL_EXPORT}
            removeCardBorder
            rowMenuActions={actions}
            filter={filterState}
        />
    );
};

export default ExcelListingsGrid;
